import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchVersionInfo, type VersionInfo } from '../lib/api';

interface UseVersionOptions {
  /**
   * Interval for version checking in milliseconds
   * @default 300000 (5 minutes)
   */
  checkInterval?: number;
}

/**
 * Custom hook to fetch version information and automatically reload on version changes
 *
 * @param options Configuration options for version checking
 * @returns Tanstack Query result with version data and loading state
 */
export const useVersion = (options: UseVersionOptions = {}) => {
  const { checkInterval = 5 * 60 * 1000 } = options; // 5 minutes

  const initialVersionRef = useRef<VersionInfo | null>(null);

  const query = useQuery({
    queryKey: ['version'],
    queryFn: fetchVersionInfo,
    refetchInterval: checkInterval,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always allow refetching
    gcTime: Infinity,
    retry: 3,
  });

  const { data: version, isLoading: loading } = query;

  // Handle version changes and auto-reload
  useEffect(() => {
    if (version) {
      // First time - store initial version and log
      if (!initialVersionRef.current) {
        initialVersionRef.current = version;

        console.info(`📦 EPD Validator ${version.shortSha}`, {
          gitSha: version.gitSha,
          shortSha: version.shortSha,
          buildTime: version.buildTime,
          environment: version.environment,
        });
      }
      // Subsequent times - check for version changes
      else if (version.gitSha !== initialVersionRef.current.gitSha) {
        console.info('🔄 New version detected, reloading...', {
          current: initialVersionRef.current.shortSha,
          new: version.shortSha,
        });
        window.location.reload();
      }
    }
  }, [version]);

  return {
    version,
    loading,
    ...query,
  };
};
